import { useFormikContext } from 'formik'
import { pick } from 'ramda'
import { useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { useQuestionnaireBackInterceptor } from '@/modules/questionnaire/providers/QuestionnaireBackInterceptor'

import type { DiffStatus } from '@/modules/app/types/HealthParameter.types'

// ================================ Types ================================

export type ObservationDiffStatus = DiffStatus

/** Status shared by the medication scheme and allergy profile wrappers. */
export type ObservationSchemeStatus = 'unknown' | 'confirmed-empty' | 'recorded'

/**
 * Minimal contract for a diff-tracked observation entry. Both
 * `MedicationStatement` and `AllergyIntolerance` satisfy it.
 */
export type DiffTrackedEntry = {
  diffStatus: ObservationDiffStatus
  previous?: unknown
}

/** An entry extended with a client-only id for stable UI identity. */
export type Identified<T> = T & { _clientId: string }

export type ObservationView = 'choice' | 'form'

export type ObservationAction = 'added' | 'edited' | 'deleted'

/** Entity-specific rules for how an edit reshapes an existing entry. */
export type EditRules<T extends DiffTrackedEntry> = {
  /**
   * Existing-entry fields preserved on edit. These are server-origin identity
   * fields the form can't reconstruct (e.g. `synopsId`, `source`, `status`);
   * everything else is taken from the freshly built entry.
   */
  identityKeys: (keyof T)[]
  /** Snapshot of the pre-edit entry, stored as `previous` for the diff. */
  buildPrevious: (existing: T) => T['previous']
}

type ObservationValue = {
  schemeStatus: ObservationSchemeStatus
} & Record<string, unknown>

// ================================ Pure transitions ================================

/** Tag an entry as a new (not-yet-persisted) insertion. */
export const markInserted = <T extends DiffTrackedEntry>(entry: T): T => ({ ...entry, diffStatus: 'inserted' }) as T

/**
 * Apply an edit to the entry at `index`, preserving the diff delta:
 *  - a still-`inserted` entry stays `inserted` (and carries no `previous`),
 *  - any other entry becomes `updated` and keeps/builds a `previous` snapshot.
 * Identity fields are kept from the existing entry; the rest comes from `next`.
 */
export const applyEdit = <T extends DiffTrackedEntry>(
  entries: T[],
  index: number,
  next: T,
  { identityKeys, buildPrevious }: EditRules<T>,
): T[] =>
  entries.map((existing, index_) => {
    if (index_ !== index) return existing
    // Keep 'inserted' status for newly added items that are edited
    const diffStatus: ObservationDiffStatus = existing.diffStatus === 'inserted' ? 'inserted' : 'updated'
    const previous = existing.diffStatus === 'inserted' ? undefined : (existing.previous ?? buildPrevious(existing))
    // Cast: the runtime shape is T (all of `next`, with identity fields kept
    // from `existing`), but TS can't prove a generic spread stays within T.
    return {
      ...next,
      ...pick(identityKeys, existing),
      diffStatus,
      previous,
    } as T
  })

/** Soft-delete the entry at `index` so the backend learns it was removed. */
export const markDeleted = <T extends DiffTrackedEntry>(entries: T[], index: number): T[] =>
  entries.map((entry, index_) => (index_ === index ? ({ ...entry, diffStatus: 'deleted' } as T) : entry))

/** Physically drop the entry at `index` (only safe for not-yet-persisted ones). */
export const removeAt = <T>(entries: T[], index: number): T[] => entries.filter((_, index_) => index_ !== index)

/**
 * Scheme status after a mutation that can empty the list: `confirmed-empty`
 * when nothing visible remains, otherwise `recorded`.
 */
export const recordedOrConfirmedEmpty = <T extends DiffTrackedEntry>(
  entries: T[],
): Extract<ObservationSchemeStatus, 'recorded' | 'confirmed-empty'> =>
  entries.some((entry) => entry.diffStatus !== 'deleted') ? 'recorded' : 'confirmed-empty'

// ================================ Hook ================================

type UseDiffTrackedObservationsParameters<T extends DiffTrackedEntry, V extends ObservationValue> = EditRules<T> & {
  /** The Formik field name / questionnaire linkId for this question. */
  questionId: string

  /** Key under which the entry array lives in the Formik value (e.g. 'medications'). */
  listKey: string

  /** Entries from the question's `value` prop, used when Formik has none yet. */
  fallbackEntries: T[]

  /** Scheme status from the question's prop, used when Formik has none yet. */
  initialSchemeStatus: ObservationSchemeStatus

  /** Analytics hook fired whenever an entry is added, edited, or deleted. */
  trackEntrySaved: (action: ObservationAction, entry: T) => void

  /** Persist the given value and advance the questionnaire. */
  submitAndAdvance: (value: V) => void
}

export type UseDiffTrackedObservationsResult<T extends DiffTrackedEntry> = {
  identified: Identified<T>[]
  visible: Identified<T>[]
  schemeStatus: ObservationSchemeStatus
  view: ObservationView
  addEntry: (entry: T) => void
  editEntry: (id: string, entry: T) => void
  removeEntry: (id: string) => void
  confirmEmpty: () => void
  setView: (view: ObservationView) => void
  onSubmitAndAdvance: () => void
}

/**
 * Shared CRUD + diff-tracking state for the medication and allergy questions.
 *
 * Both questions hold a list of entries that each carry a per-submission
 * `diffStatus` (unchanged/updated/inserted/deleted) plus an optional `previous`
 * snapshot. The diff transitions are the pure helpers above; this hook wires
 * them to Formik, the analytics event, and the choice/form view state.
 *
 * Stable client-only ids are kept in a ref, updated in lockstep with Formik
 * mutations, and never written to Formik so they can't reach the API.
 */
export const useDiffTrackedObservations = <T extends DiffTrackedEntry, V extends ObservationValue>({
  questionId,
  listKey,
  fallbackEntries,
  initialSchemeStatus,
  identityKeys,
  buildPrevious,
  trackEntrySaved,
  submitAndAdvance,
}: UseDiffTrackedObservationsParameters<T, V>): UseDiffTrackedObservationsResult<T> => {
  const { setFieldValue, values } = useFormikContext<Record<string, unknown>>()

  const formikValue = values[questionId] as V | undefined
  const schemeStatus = formikValue?.schemeStatus ?? initialSchemeStatus
  const entries =
    ((formikValue as Record<string, unknown> | undefined)?.[listKey] as T[] | undefined) ?? fallbackEntries

  // Stable client-only ids, one per entry by position. Held in state and
  // adjusted while rendering when the entry count changes (React's documented
  // "adjust state while rendering" pattern): existing ids are reused by
  // position and any new ones generated once. This keeps the sync
  // concurrent-safe — nothing is mutated mid-render — and the ids are never
  // written to Formik, so they can't reach the API.
  const [clientIds, setClientIds] = useState<string[]>(() => entries.map(() => uuidv4()))
  let ids = clientIds
  if (clientIds.length !== entries.length) {
    ids = entries.map((_, index) => clientIds[index] ?? uuidv4())
    setClientIds(ids)
  }

  const identified = useMemo<Identified<T>[]>(
    () =>
      entries.map((entry, index) => ({
        ...entry,
        _clientId: ids[index],
      })),
    [entries, ids],
  )

  const visible = useMemo(() => identified.filter((entry) => entry.diffStatus !== 'deleted'), [identified])

  const [viewOverride, setViewOverride] = useState<ObservationView | null>(null)
  const view = viewOverride ?? (schemeStatus === 'unknown' ? 'choice' : 'form')

  const writeToFormik = (newSchemeStatus: ObservationSchemeStatus, items: T[]) => {
    setFieldValue(questionId, {
      schemeStatus: newSchemeStatus,
      [listKey]: items,
    })
  }

  const addEntry = (entry: T) => {
    // The new entry's id is assigned by the position-based sync above on the
    // next render (entry count grows).
    writeToFormik('recorded', [...entries, markInserted(entry)])
    trackEntrySaved('added', entry)
  }

  const editEntry = (id: string, next: T) => {
    const index = ids.indexOf(id)
    if (index === -1) return
    const updated = applyEdit(entries, index, next, {
      identityKeys,
      buildPrevious,
    })
    writeToFormik('recorded', updated)
    trackEntrySaved('edited', updated[index])
  }

  const removeEntry = (id: string) => {
    const index = ids.indexOf(id)
    if (index === -1) return
    const target = entries[index]

    // Newly inserted items can be fully removed — they don't exist on the
    // backend yet; existing items are soft-deleted so the backend learns.
    // The id list re-aligns by position on the next render.
    const updated = target.diffStatus === 'inserted' ? removeAt(entries, index) : markDeleted(entries, index)

    writeToFormik(recordedOrConfirmedEmpty(updated), updated)
    trackEntrySaved('deleted', target)
  }

  const confirmEmpty = () => {
    writeToFormik('confirmed-empty', [])
    submitAndAdvance({ schemeStatus: 'confirmed-empty', [listKey]: [] } as V)
  }

  const setView = (newView: ObservationView) => {
    if (newView === 'form') {
      writeToFormik('recorded', entries)
    }
    setViewOverride(newView)
  }

  // Return from the add-flow to the initial choice. Entering the form committed
  // 'recorded'; revert to the pristine 'unknown' state (keeping any entered
  // items) so the choice question shows again.
  const backToChoiceRef = useRef<() => void>()
  backToChoiceRef.current = () => {
    writeToFormik('unknown', entries)
    setViewOverride(null)
  }

  // While in the add-flow reached from the choice (no known data), let the
  // questionnaire's shared back action return to that choice instead of leaving
  // the question.
  const setBackInterceptor = useQuestionnaireBackInterceptor()
  useEffect(() => {
    if (initialSchemeStatus !== 'unknown' || view !== 'form') return
    setBackInterceptor(() => {
      // Clear immediately so a quick second back/swipe isn't consumed by this
      // (now stale) interceptor before the effect cleanup runs.
      setBackInterceptor(null)
      backToChoiceRef.current?.()
      return true
    })
    return () => setBackInterceptor(null)
  }, [initialSchemeStatus, view, setBackInterceptor])

  const onSubmitAndAdvance = () => {
    const currentValue = formikValue ?? ({ schemeStatus, [listKey]: entries } as V)
    submitAndAdvance(currentValue)
  }

  return {
    identified,
    visible,
    schemeStatus,
    view,
    addEntry,
    editEntry,
    removeEntry,
    confirmEmpty,
    setView,
    onSubmitAndAdvance,
  }
}
